import React, { memo, useState } from 'react';
import styles from '../../styles.module.scss';
import { Box, MenuItem } from '@mui/material';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  Modal,
  TabFields
} from '@cmhc-schl/hearth-ui';
import { useTranslation } from 'react-i18next';
import HbtMasterPoolLoansInventoryDetail from '../../../HbtMasterPoolLoansInventoryDetail/HbtMasterPoolLoansInventoryDetail';
import { LoanInventoryTableFields } from '../../types';
import Popupmenu from 'Feature/V2/Shared_Portfolio/components/PopupMenu/Popupmenu';

interface ActionDetailsButtonsProps {
  isViewLoanDetailsButtonVisible: boolean;
  selectedRowsData: LoanInventoryTableFields[];
  handleMutipleLoanDetailsActionClick: (tabList: TabFields[]) => void;
  toggleRightPane: () => void;
  setOpenForceStatusFlyout: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenAddRemarksFlyout: React.Dispatch<React.SetStateAction<boolean>>;
  openHistoricalFlyout?: () => void;
}

const ActionDetailsButtons = ({
  isViewLoanDetailsButtonVisible,
  selectedRowsData,
  handleMutipleLoanDetailsActionClick,
  toggleRightPane,
  setOpenForceStatusFlyout,
  setOpenAddRemarksFlyout,
  openHistoricalFlyout
}: ActionDetailsButtonsProps) => {
  const { t } = useTranslation('HbtMasterPool');
  const { t: ro } = useTranslation('HbtPortfolioManagementRequestOverview');
  const MAX_NUM_OF_LOAN_OPEN_AT_ONCE = 10;
  const [isSelectionLimitModalOpen, setIsSelectionLimitModalOpen] = useState(false);

  const [anchorElementActionsButton, setAnchorElementActionsButton] = useState<null | HTMLElement>(
    null
  );

  const isNotSameStatus = Boolean(
    selectedRowsData.find((data) => selectedRowsData[0].loanStatus !== data.loanStatus)
  );

  const handleCloseMenu = () => {
    setAnchorElementActionsButton(null);
  };
  const selectedRowsWithHistoricalData = selectedRowsData.filter(
    (row) => row.remarks && row.remarks?.length > 0
  );

  const handleHistoricalFlyout = () => {
    if (selectedRowsWithHistoricalData.length > MAX_NUM_OF_LOAN_OPEN_AT_ONCE) {
      setIsSelectionLimitModalOpen(true);
      handleCloseMenu();
      return;
    }
    if (openHistoricalFlyout) {
      openHistoricalFlyout();
    }
    handleCloseMenu();
  };

  return (
    <>
      <Modal
        id={'selction-limit-reached'}
        isActive={isSelectionLimitModalOpen}
        onDismissalCallback={() => {
          setIsSelectionLimitModalOpen(false);
        }}
        title={{ value: ro('Selection-Limit-Reached') }}
        bodyContent={t('Selection-Limit-Body-Text-Historical_Data')}
        shouldCloseOnBackgroundClick={true}
      />

      <Box className={styles.actionDetailsButtonsWrapper}>
        <Button
          type="button"
          name="overrideStatus"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.TERTIARY}
          leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_override" />}
          text={`${t('Override-Status')} ${selectedRowsData.length ? `(${selectedRowsData.length})` : ''}`}
          ariaText={{
            value: t('Override-Status')
          }}
          className={styles.button}
          onClick={() => setOpenForceStatusFlyout(true)}
        />
        <Button
          type="button"
          name="masterPoolrightPaneDetails"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.TERTIARY}
          leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_details" />}
          text={{ value: t('PI-Portfolio-Management-Request-Overview-Details') }}
          ariaText={{
            value: t('PI-Portfolio-Management-Request-Overview-Details')
          }}
          className={styles.button}
          onClick={toggleRightPane}
        />
        {selectedRowsData.length >= 1 && (
          <>
            <Button
              type="button"
              onClick={(evt: React.MouseEvent<HTMLElement>) =>
                setAnchorElementActionsButton(evt.currentTarget)
              }
              name="actions"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
              leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_more_vertical" />}
              text={{ value: ro('PI-Portfolio-Management-Request-Overview-Actions') }}
              ariaText={{
                value: ro('PI-Portfolio-Management-Request-Overview-Actions')
              }}
              className={`${styles.button} ${styles.popupMenuContainer}`}
            />
            <Popupmenu
              anchorElement={anchorElementActionsButton}
              className={styles.popupMenuContainer}
              closeMenu={handleCloseMenu}
            >
              <>
                {selectedRowsWithHistoricalData.length > 0 && (
                  <MenuItem onClick={handleHistoricalFlyout} className={styles.popupMenu}>
                    <CmhcIcon size={CmhcIconSize.SMALL} type="icon_history" />
                    <span className={styles.working}>
                      {t('View-Historical-Data')} (
                      {t('Count-Available', {
                        count: selectedRowsWithHistoricalData.length
                      })}
                      )
                    </span>
                  </MenuItem>
                )}
                {!isNotSameStatus && (
                  <MenuItem
                    onClick={() => {
                      setOpenAddRemarksFlyout(true);
                      handleCloseMenu();
                    }}
                    className={styles.popupMenu}
                  >
                    <CmhcIcon size={CmhcIconSize.SMALL} type="icon_comment_add" />
                    <span>
                      {t('Add-Remarks-Action-Menu')} (
                      {ro('Selected', { count: selectedRowsData.length })})
                    </span>
                  </MenuItem>
                )}
              </>
            </Popupmenu>
          </>
        )}
        {isViewLoanDetailsButtonVisible && (
          <Button
            type="button"
            name="details"
            size={ButtonSize.SMALL}
            onClick={() => {
              const tabs = selectedRowsData.map((row) => {
                const tabField: TabFields = {
                  label: { value: row.cmhcAccountNumber },
                  content: (
                    <HbtMasterPoolLoansInventoryDetail
                      loanReferenceNumber={row.lenderReferenceNumber}
                      loanPortfolioEpipProcessingStatus={row.loanStatus}
                      cmhcAccountNumber={row.cmhcAccountNumber}
                      loanInventoryData={row}
                    />
                  )
                };
                return tabField;
              });
              handleMutipleLoanDetailsActionClick(tabs);
            }}
            variant={ButtonVariant.PRIMARY}
            text={{ value: `${t('View-Loan-Details')} (${selectedRowsData.length})` }}
            ariaText={{
              value: t('PI-Portfolio-Management-Request-Overview-Details')
            }}
            className={styles.viewLoanDetailsButton}
          />
        )}
      </Box>
    </>
  );
};

export default memo(ActionDetailsButtons);
